import { ChangeEvent, useState } from 'react'
import { useLazyLoadQuery } from 'react-relay'
import { graphql } from 'relay-runtime'

import { HookText } from '../HookText/HookText'
import { HookTextsQuery, KPI } from './__generated__/HookTextsQuery.graphql'

export const hookTextInsightsQuery = graphql`
  query HookTextsQuery($input: ListInsightV2Request!) {
    hookTexts: listInsightV2(input: $input) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      data {
        id
        ...HookText_InsightV2
      }

      metadata {
        dateRange {
          start
          end
        }
      }
    }
  }
`

interface HookTextInsightsProps {
  brandId: string
}
const PAGE_SIZE = 20
export const HookTextInsights = (props: HookTextInsightsProps) => {
  const { brandId } = props
  const [targetKpi, setTargetKpi] = useState<KPI>('AVG_DAILY_SPEND')
  const [skip, setSkip] = useState(0)

  const data = useLazyLoadQuery<HookTextsQuery>(
    hookTextInsightsQuery,
    {
      input: {
        pagination: {
          skip,
          take: PAGE_SIZE,
        },
        filter: {
          brandId,
          labelTypes: ['HOOK_TEXT'],
          delta_gt: 0,
          kpi: targetKpi,
          includeAllDates: true,
        },
        orderBy: 'LABEL_LIFT_DESC',
      },
    },
    { fetchPolicy: 'store-and-network', fetchKey: targetKpi }
  )

  function handleKpiChange(e: ChangeEvent<HTMLSelectElement>) {
    const val = e.currentTarget.value as KPI

    setTargetKpi(val)
  }

  const showEmptyState = !data?.hookTexts.data.length
  const startDate = data?.hookTexts.metadata.dateRange?.start
  const [year, month] = startDate?.split('-') || []
  const endDate = data?.hookTexts.metadata.dateRange?.end
  const [endYear, endMonth] = endDate?.split('-') || []
  const count = data.hookTexts.count
  const { hasNextPage, hasPreviousPage } = data.hookTexts.pageInfo

  const currentPage = skip / PAGE_SIZE + 1
  const totalPages = Math.ceil(count / PAGE_SIZE) || 1

  return (
    <div className="px-8">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <h5 className="text-2xl">Hook Text Performance</h5>
          {startDate && endDate && (
            <>
              <input
                className="ml-4 text-center"
                type="month"
                value={`${year}-${month}`}
                disabled
              />
              <span>to</span>
              <input
                className="text-center"
                type="month"
                value={`${endYear}-${endMonth}`}
                disabled
              />
            </>
          )}
        </div>

        <p>
          The following text overlays appeared in the first ~5 seconds of
          videos.
        </p>

        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap">Target KPI:</span>
          <select
            className="block rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 "
            defaultValue={targetKpi}
            onChange={handleKpiChange}
          >
            <option value="AVG_DAILY_SPEND">Avg. Daily Spend</option>
            <option value="HOOK_RATE">Hook rate</option>
            <option value="AVG_DAILY_PURCHASE_VOLUME">
              Avg. Daily purchase volume
            </option>
          </select>
        </div>
      </div>

      <div className="mt-6 overflow-hidden">
        <div className="flex gap-12 overflow-x-scroll py-2">
          {showEmptyState && (
            <div className="text-midGray">
              We don't have any insights for this KPI. Try another!
            </div>
          )}
          {data.hookTexts?.data.map((insight) => (
            <HookText fragmentKey={insight} key={insight.id} />
          ))}
        </div>
      </div>
      <div className="mt-4 flex justify-center gap-2 pb-2">
        {hasPreviousPage && (
          <button onClick={() => setSkip(skip - PAGE_SIZE)} className="text-xs">
            Prev
          </button>
        )}

        <div>
          Page {currentPage} of {totalPages}
        </div>

        {hasNextPage && (
          <button onClick={() => setSkip(skip + PAGE_SIZE)} className="text-xs">
            Next
          </button>
        )}
      </div>
    </div>
  )
}
